import { AUDIT_ACTION, type AuditAction } from './common.js';
import type { NotificationType, NotifyInput } from './notifications.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DENETİM KAYDI → BİLDİRİM KÖPRÜSÜ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ürünün "kayda değer an" listesi zaten vardı: `AUDIT_ACTION` (78 eylem) ve
 * onu yazan TEK bir nokta (`AuditService.record`). Bildirimler ise 46 tipti
 * ve ikisi arasında kocaman bir boşluk duruyordu — üye çıkarıldığında, kural
 * silindiğinde, ödeme alındığında, oturum kapatıldığında denetim defterine
 * satır düşüyor ama KİMSEYE haber gitmiyordu.
 *
 * ---------------------------------------------------------------------------
 * 🔴 Neden 30 servise tek tek `notify()` eklenmedi
 * ---------------------------------------------------------------------------
 * Üç sebep, üçü de ölçülmüş:
 *
 *   1. AYNI HATA SINIFI. Bu turda ölçülen üç canlı kusurun üçü de dağıtık
 *      `notify()` çağrılarından doğmuştu: eksik olay yükü, metinle uyuşmayan
 *      parametre adı, bir yerde iki kez gönderim. Otuz yeni çağrı, o üç
 *      hatanın otuz yeni ihtimali demekti.
 *   2. DRIFT. Yeni bir servis denetim kaydı yazdığında bildirim eklemeyi
 *      kimse hatırlamaz; burada ise eylem ya haritada vardır ya yoktur ve
 *      bunu bir test sayar.
 *   3. SÖZLEŞME TESTİ MÜMKÜN OLUR. Her kural SAF bir fonksiyon: sentetik bir
 *      yükle çağrılıp ürettiği parametrelerin, metnin beklediği yer
 *      tutucuları KARŞILADIĞI test edilebiliyor (`notification-audit.spec.ts`).
 *      Dağıtık çağrılarda böyle bir test yazılamaz.
 *
 * ---------------------------------------------------------------------------
 * 🔴 Haritada OLMAYAN eylemler — ve neden yoklar
 * ---------------------------------------------------------------------------
 * Harita bir İZİN LİSTESİ: yalnız burada adı geçen eylem bildirim üretir.
 * Kasten dışarıda bırakılanlar:
 *
 *   • `CAMPAIGN_STATUS_CHANGED`, `BUDGET_UPDATED`, `CAMPAIGN_CREATED` —
 *     bunları `campaigns.service` / `rmf.service` ZATEN doğrudan gönderiyor.
 *     Haritaya da konsalardı her kampanya işlemi İKİ bildirim üretirdi.
 *   • `PASSWORD_CHANGED` — tarihî; parola 10 Eki 2026'da kalktı, eylem artık
 *     hiç yazılmıyor.
 *   • `CREATIVE_APPROVAL_*` — `creative-approval.service` gönderiyor.
 *   • `PROFILE_UPDATED`, `NOTIFICATION_PREFS_UPDATED`, `REPORT_EXPORTED`,
 *     `ACCOUNT_SYNC_REQUESTED`, `STORE_SYNC_REQUESTED`, `MEETING_BOOKING_OPENED` —
 *     kullanıcının SANİYESİ ÖNCE kendi yaptığı, sonucu ekranda anında görünen
 *     işler. Bunlara bildirim, kullanıcıya kendi tıklamasını geri okumaktır.
 *   • `WORKSPACE_DELETED` — bildirim satırı silinen alana ait olurdu
 *     (`onDelete: Cascade`), yani aynı işlemde silinirdi.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Şekiller
// ═══════════════════════════════════════════════════════════════════════════

export interface AuditNotificationPayload {
  action: AuditAction;
  workspaceId: string;
  /// Eylemi yapan kişi. `null` olabilir: webhook ve zamanlanmış işler
  /// (`PAYMENT_RECORDED`, `INVOICE_ISSUED`) kimse adına çalışmıyor.
  actorUserId: string | null;
  targetId: string | null;
  before: unknown;
  after: unknown;
}

export interface AuditNotification {
  type: NotificationType;
  /**
   * Kime gidecek.
   *
   *   `catalog` (varsayılan) → `NOTIFICATION_CATALOG`taki `audience` (rol bazlı)
   *   `actor`               → eylemi YAPAN kişi (oturum kapatma, destek yanıtı)
   *   `target`              → `targetId`nin işaret ettiği KULLANICI
   *
   * 🔴 `target` yalnız hedefi bir kullanıcı olan eylemlerde geçerli
   * (`MEMBER_ROLE_CHANGED` → rolü değişen kişi). Yanlış kullanılırsa bildirim
   * bir kampanya kimliğine gönderilmeye çalışılır ve sessizce düşer.
   */
  to?: 'catalog' | 'actor' | 'target';
  params?: Record<string, string | number>;
  data?: NonNullable<NotifyInput['data']>;
  /// Gürültü penceresi (saniye). Yoksa dedupe uygulanmıyor — her kayıt kendi
  /// bildirimini üretir.
  dedupeSeconds?: number;
  /**
   * Metin `{workspaceName}` istiyor mu. Denetim yükünde alan adı yok; köprü
   * gerektiğinde tek bir sorguyla ekliyor. Bayrak olmadan HER bildirim için
   * bir sorgu atmak gerekirdi.
   */
  needsWorkspaceName?: boolean;
}

/// Kural SAF bir fonksiyon. `null` → "bu kayıt bildirim üretmiyor"; aynı
/// eylemin iki farklı yükü olabiliyor (`SESSION_REVOKED` hem tek oturumu hem
/// hepsini kapatan yönetici işlemi için yazılıyor) ve ayrımı kural yapıyor.
export type AuditNotificationRule = (payload: AuditNotificationPayload) => AuditNotification | null;

// ═══════════════════════════════════════════════════════════════════════════
// Yük okuyucular — denetim yükü `Json`, yani `unknown`
// ═══════════════════════════════════════════════════════════════════════════

function rec(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/// Dize alan. Boş dize ve `null` AYNI sayılıyor: ikisi de metindeki yer
/// tutucuyu dolduramaz ve arayüz o durumda parametresiz cümleye düşüyor.
function str(source: unknown, key: string): string | null {
  const value = rec(source)[key];
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'number') return String(value);
  return null;
}

function num(source: unknown, key: string): number | null {
  const value = rec(source)[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  // Prisma `Decimal` alanları denetim kaydına dize olarak düşebiliyor.
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}

/**
 * `before` ile `after` arasında DEĞİŞEN alan adları.
 *
 * `workspace.updated` gövdesi bunu basıyor: "Değişen: name, maxBudgetChangePercent".
 * Değerleri yazmıyoruz — denetim kaydı ham ayar nesnesi taşıyor ve içinde
 * kullanıcıya gösterilmemesi gereken alanlar olabilir; ADLAR ise kullanıcının
 * zaten ekranda gördüğü şeyler.
 */
export function changedFields(before: unknown, after: unknown): string[] {
  const b = rec(before);
  const a = rec(after);
  const keys = new Set([...Object.keys(b), ...Object.keys(a)]);
  const changed: string[] = [];
  for (const key of keys) {
    if (JSON.stringify(b[key]) !== JSON.stringify(a[key])) changed.push(key);
  }
  return changed.sort();
}

/// Kullanıcının işine yaramayan, tek başına bir "ayar değişti" bildirimi
/// doğurmaması gereken alanlar. Onboarding adımı bunların başında geliyor:
/// `WORKSPACE_UPDATED` kaydını onboarding'i atlamak da yazıyor.
const SILENT_WORKSPACE_FIELDS = new Set([
  'onboardingSkippedAt',
  'onboardingCompletedAt',
  'updatedAt',
  /* Faz 7c — tam otonomi onayının sürüm kaydı bir AYAR değil; bildirim
     "fullAutonomy" değişikliğini zaten söylüyor. */
  'fullAutonomyConsent',
]);

// ═══════════════════════════════════════════════════════════════════════════
// Harita
// ═══════════════════════════════════════════════════════════════════════════

const DAY = 24 * 60 * 60;

export const AUDIT_NOTIFICATIONS: Partial<Record<AuditAction, AuditNotificationRule>> = {
  // ── Ekip ────────────────────────────────────────────────────────────────
  [AUDIT_ACTION.MEMBER_INVITED]: (p) => {
    const email = str(p.after, 'email');
    if (!email) return null;
    return {
      type: 'team.invitation_sent',
      params: { email, role: str(p.after, 'role') ?? '' },
      data: p.targetId ? { entity: 'member', id: p.targetId } : undefined,
    };
  },
  [AUDIT_ACTION.MEMBER_INVITE_RESENT]: (p) => {
    const email = str(p.after, 'email');
    return email ? { type: 'team.invitation_resent', params: { email } } : null;
  },
  [AUDIT_ACTION.MEMBER_INVITE_REVOKED]: (p) => {
    const email = str(p.before, 'email');
    return email ? { type: 'team.invitation_revoked', params: { email } } : null;
  },
  [AUDIT_ACTION.MEMBER_JOINED]: (p) => {
    const email = str(p.after, 'email');
    if (!email) return null;
    return {
      type: 'team.member_joined',
      // Metin `{name}` bekliyor; katılan kişinin adı bu noktada yok, adresi
      // var. Adres bir addan daha az okunur ama daha AYIRT EDİCİ.
      params: { name: email, role: str(p.after, 'role') ?? '' },
      data: p.targetId ? { entity: 'member', id: p.targetId } : undefined,
    };
  },
  [AUDIT_ACTION.MEMBER_ROLE_CHANGED]: (p) => {
    const role = str(p.after, 'role');
    if (!role) return null;
    return {
      // 🔴 `to: 'target'` — rolü DEĞİŞEN kişiye gidiyor, değiştirene değil.
      // Katalogdaki `audience: 'specific_user'` tam olarak bunu söylüyor ama
      // "o kullanıcı kim" bilgisi yalnız denetim kaydında var.
      to: 'target',
      type: 'team.role_changed',
      params: { role },
      needsWorkspaceName: true,
    };
  },
  [AUDIT_ACTION.MEMBER_REMOVED]: (p) => {
    const email = str(p.before, 'email');
    if (!email) return null;
    return {
      type: 'team.member_removed',
      params: { email, role: str(p.before, 'role') ?? '' },
    };
  },
  [AUDIT_ACTION.MEMBER_LEFT]: (p) => {
    const name = str(p.before, 'email') ?? str(p.before, 'name');
    return name ? { type: 'team.member_left', params: { name } } : null;
  },
  [AUDIT_ACTION.OWNERSHIP_TRANSFERRED]: (p) => {
    const email = str(p.after, 'ownerEmail');
    return email ? { type: 'team.ownership_transferred', params: { email } } : null;
  },

  // ── Çalışma alanı ───────────────────────────────────────────────────────
  [AUDIT_ACTION.WORKSPACE_CREATED]: (p) => {
    const name = str(p.after, 'name');
    return name ? { type: 'workspace.created', params: { name } } : null;
  },
  [AUDIT_ACTION.WORKSPACE_UPDATED]: (p) => {
    const changed = changedFields(p.before, p.after).filter(
      (field) => !SILENT_WORKSPACE_FIELDS.has(field),
    );
    // Yalnız onboarding adımı değiştiyse bildirim YOK: o kayıt kullanıcının
    // "şimdilik atla"ya basmasından doğuyor ve ayar değişikliği değil.
    if (changed.length === 0) return null;
    return { type: 'workspace.updated', params: { changes: changed.join(', ') } };
  },

  // ── Bağlantılar ─────────────────────────────────────────────────────────
  [AUDIT_ACTION.CONNECTION_CREATED]: (p) => {
    const platform = str(p.after, 'platform');
    if (!platform) return null;
    return {
      type: 'connection.created',
      params: { platform },
      data: p.targetId ? { entity: 'connection', id: p.targetId } : undefined,
    };
  },
  [AUDIT_ACTION.CONNECTION_RECONNECTED]: (p) => {
    const platform = str(p.after, 'platform');
    if (!platform) return null;
    // Mevcut `connection.recovered` tipi YENİDEN KULLANILIYOR: kullanıcı
    // açısından sonuç aynı — bağlantı yeniden çalışıyor. Ayrı bir tip,
    // tercih ekranında ayırt edilemeyen ikinci bir satır olurdu.
    return {
      type: 'connection.recovered',
      params: { platform },
      data: p.targetId ? { entity: 'connection', id: p.targetId } : undefined,
    };
  },
  [AUDIT_ACTION.CONNECTION_REMOVED]: (p) => {
    const platform = str(p.before, 'platform');
    return platform ? { type: 'connection.removed', params: { platform } } : null;
  },
  [AUDIT_ACTION.ACCOUNT_CONNECTED]: (p) => {
    const count = num(p.after, 'count');
    if (!count) return null;
    return {
      type: 'connection.account_connected',
      params: { count },
      data: p.targetId ? { entity: 'connection', id: p.targetId } : undefined,
    };
  },
  [AUDIT_ACTION.STORE_CONNECTED]: (p) => {
    const platform = str(p.after, 'platform');
    return platform ? { type: 'connection.store_connected', params: { platform } } : null;
  },
  [AUDIT_ACTION.STORE_DISCONNECTED]: (p) => {
    const platform = str(p.before, 'platform');
    return platform ? { type: 'connection.store_disconnected', params: { platform } } : null;
  },

  // ── Otomasyon ───────────────────────────────────────────────────────────
  [AUDIT_ACTION.WORKFLOW_CREATED]: (p) => {
    const ruleName = str(p.after, 'name');
    if (!ruleName) return null;
    return {
      type: 'automation.rule_created',
      params: { ruleName },
      data: p.targetId ? { entity: 'workflow', id: p.targetId } : undefined,
    };
  },
  [AUDIT_ACTION.WORKFLOW_ENABLED]: (p) => {
    const ruleName = str(p.after, 'name');
    if (!ruleName) return null;
    return {
      type: 'automation.rule_enabled',
      params: { ruleName },
      data: p.targetId ? { entity: 'workflow', id: p.targetId } : undefined,
    };
  },
  [AUDIT_ACTION.WORKFLOW_DISABLED]: (p) => {
    const ruleName = str(p.after, 'name');
    if (!ruleName) return null;
    return {
      type: 'automation.rule_disabled',
      params: { ruleName },
      data: p.targetId ? { entity: 'workflow', id: p.targetId } : undefined,
    };
  },
  [AUDIT_ACTION.WORKFLOW_DELETED]: (p) => {
    const ruleName = str(p.before, 'name');
    // Silinen kuralın sayfası artık yok — `data` VERİLMİYOR, yoksa bildirim
    // 404'e tıklatırdı.
    return ruleName ? { type: 'automation.rule_deleted', params: { ruleName } } : null;
  },
  [AUDIT_ACTION.ACTION_REJECTED]: (p) => ({
    type: 'automation.action_rejected',
    params: { targetName: str(p.after, 'targetName') ?? '' },
    data: p.targetId ? { entity: 'workflow_action', id: p.targetId } : undefined,
  }),
  [AUDIT_ACTION.ACTION_ROLLED_BACK]: (p) => ({
    type: 'automation.action_rolled_back',
    params: { reason: str(p.after, 'reason') ?? '' },
    data: p.targetId ? { entity: 'workflow_action', id: p.targetId } : undefined,
  }),

  // ── Faturalama ──────────────────────────────────────────────────────────
  [AUDIT_ACTION.BILLING_PLAN_CHANGED]: (p) => {
    const plan = str(p.after, 'plan');
    return plan
      ? {
          type: 'billing.plan_changed',
          params: { plan },
          data: { entity: 'invoice', id: p.workspaceId },
        }
      : null;
  },
  [AUDIT_ACTION.TOPUP_PURCHASED]: (p) => {
    const credits = num(p.after, 'credits');
    return credits
      ? {
          type: 'billing.topup_purchased',
          params: { credits },
          data: { entity: 'invoice', id: p.workspaceId },
        }
      : null;
  },
  [AUDIT_ACTION.PAYMENT_RECORDED]: (p) => {
    const amount = num(p.after, 'amount');
    if (amount === null) return null;
    return {
      type: 'billing.payment_recorded',
      params: { amount, currency: str(p.after, 'currency') ?? '' },
      data: { entity: 'invoice', id: p.workspaceId },
    };
  },
  [AUDIT_ACTION.PAYMENT_REFUNDED]: (p) => {
    const amount = num(p.after, 'amount') ?? num(p.after, 'refundedAmount');
    if (amount === null) return null;
    return {
      type: 'billing.payment_refunded',
      params: { amount, currency: str(p.after, 'currency') ?? str(p.before, 'currency') ?? '' },
      data: { entity: 'invoice', id: p.workspaceId },
    };
  },
  /**
   * Tahsilat reddedildi. Tutar ve sebep gövdeye GİRMİYOR: katalog metni
   * parametresiz ("Son ödeme denemesi geçmedi…") ve sebep kodu çevrilmeden
   * e-postaya basılırsa `INSUFFICIENT_FUNDS` diye okunurdu. Ayrıntı panelde.
   *
   * YALNIZ OTOMATİK tahsilatta (`after.automatic === true` — zamanlanmış
   * yenileme). Kullanıcının başlattığı ödeme reddedildiğinde kişi o an
   * ekranda ve sebebi orada okuyor; her yanlış SMS kodu için e-posta/SMS
   * göndermek bildirimi gürültüye çevirirdi.
   */
  [AUDIT_ACTION.PAYMENT_FAILED]: (p) =>
    rec(p.after).automatic === true
      ? { type: 'billing.payment_failed', data: { entity: 'invoice', id: p.workspaceId } }
      : null,
  [AUDIT_ACTION.PAYMENT_DISPUTED]: (p) => {
    const amount = num(p.after, 'amount') ?? num(p.before, 'amount');
    if (amount === null) return null;
    return {
      type: 'billing.payment_disputed',
      params: { amount, currency: str(p.after, 'currency') ?? str(p.before, 'currency') ?? '' },
      data: { entity: 'invoice', id: p.workspaceId },
    };
  },
  [AUDIT_ACTION.INVOICE_ISSUED]: (p) => {
    const documentNo = str(p.after, 'documentNo');
    // Belge numarası YOKSA bildirim yok: "Fatura kesildi" deyip numarasını
    // söyleyememek, kullanıcıyı faturayı aramaya gönderir.
    return documentNo
      ? {
          type: 'billing.invoice_issued',
          params: { documentNo },
          data: { entity: 'invoice', id: p.workspaceId },
        }
      : null;
  },

  // ── Kreatif ─────────────────────────────────────────────────────────────
  [AUDIT_ACTION.CREATIVE_PUBLISHED]: (p) => {
    const creativeName = str(p.after, 'name');
    if (!creativeName) return null;
    return {
      type: 'creative.published',
      params: { creativeName },
      data: p.targetId ? { entity: 'creative', id: p.targetId } : undefined,
    };
  },

  // ── Hesap güvenliği ─────────────────────────────────────────────────────
  [AUDIT_ACTION.EMAIL_CHANGE_REQUESTED]: (p) => {
    const email = str(p.after, 'email');
    if (!email) return null;
    return {
      // 🔴 `to: 'actor'` ve bu ÖNEMLİ: bildirim ESKİ adrese gidiyor, çünkü
      // `User.email` henüz değişmedi (değişiklik onaydan sonra). Hesabı ele
      // geçiren biri adresi değiştirmeye kalkarsa, gerçek sahibi tam bu anda
      // haber alıyor.
      to: 'actor',
      type: 'account.email_change_requested',
      params: { email },
      data: p.actorUserId ? { entity: 'account', id: p.actorUserId } : undefined,
    };
  },
  /* Dönüş tipi AÇIKÇA yazılı: iki dal iki farklı `params` şekli üretiyor ve
     TypeScript birleşimi daraltınca `{ device: string; count?: undefined }`
     gibi bir tip çıkarıp `Record<string, string | number>` ile uyuşmuyor. */
  [AUDIT_ACTION.SESSION_REVOKED]: (p): AuditNotification | null => {
    const device = str(p.after, 'device');
    if (device) {
      return {
        to: 'actor',
        type: 'account.session_revoked',
        params: { device },
        data: p.actorUserId ? { entity: 'account', id: p.actorUserId } : undefined,
      };
    }
    /* Yönetici tarafı (`/admin/users/:id`) bu eylemi kullanıcının TÜM
       oturumlarını kapatmak için de yazıyor ve yükünde cihaz değil sayı var.
       Kullanıcı açısından bu bambaşka bir olay — "bir oturumun kapandı" değil,
       "hepsi kapandı" — ve ayrı tipe düşüyor. */
    const count = num(p.after, 'revokedCount');
    if (count === null) return null;
    return {
      to: 'target',
      type: 'account.sessions_revoked',
      params: { count },
      data: p.targetId ? { entity: 'account', id: p.targetId } : undefined,
    };
  },
  [AUDIT_ACTION.SESSIONS_REVOKED_OTHERS]: (p) => {
    const count = num(p.after, 'revokedCount');
    if (count === null) return null;
    return {
      to: 'actor',
      type: 'account.sessions_revoked',
      params: { count },
      data: p.actorUserId ? { entity: 'account', id: p.actorUserId } : undefined,
    };
  },

  // ── Destek ──────────────────────────────────────────────────────────────
  // Destek yanıtı denetim kaydı YAZMIYOR (bilet mesajı zaten tabloda duruyor),
  // bu yüzden `support.*` tipleri köprüden değil `support.service`ten
  // gönderiliyor. Harita orayı kapsamıyor; buradaki not, arayanın boşuna
  // aramaması için.
};

/// Dedupe anahtarı — köprü bunu kullanıyor. Eylem + hedef: aynı hedefe aynı
/// eylemden iki kayıt düşerse (yeniden deneme) tek bildirim çıkıyor.
export function auditDedupeKey(payload: AuditNotificationPayload): string {
  return `audit:${payload.action}:${payload.targetId ?? 'none'}`;
}

/// Köprünün varsayılan penceresi. Kural kendi `dedupeSeconds`ını verirse o
/// geçerli. Bir gün DEĞİL, 60 saniye: aynı eylemin gün içinde tekrar olması
/// normal (üç kural açıldı, iki ödeme alındı) ve hepsi bildirilmeli; pencere
/// yalnız çift yazmaya karşı.
export const AUDIT_DEDUPE_SECONDS = 60;

/// Bir günlük pencere isteyen kurallar için hazır sabit.
export const AUDIT_DEDUPE_DAILY = DAY;
